import React, { useState, useEffect } from 'react';
import {
  FaSearch, FaCalendarAlt, FaClock, FaCheck, FaTimes,
  FaTrash, FaCalculator, FaCheckCircle, FaHourglassHalf
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/RHStatsDetailPage';
import { useAuth } from '../context/AuthContext';


const modalStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  },
  content: {
    backgroundColor: 'white', borderRadius: '15px', padding: '20px',
    maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 5px 20px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '2px solid #1B019B', paddingBottom: '10px', marginBottom: '20px'
  },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }
};

const tabStyles = {
  container: { borderBottom: '2px solid #1B019B', marginBottom: '20px', display: 'flex', gap: '5px' },
  tab: {
    padding: '10px 20px', cursor: 'pointer', border: 'none', background: 'none',
    fontSize: '16px', fontWeight: '500', color: '#666',
    borderBottom: '2px solid transparent', transition: 'all 0.3s'
  },
  activeTab: { color: '#1B019B', borderBottom: '2px solid #1B019B', fontWeight: '600' }
};

// Fonctions utilitaires
const heuresEnMinutes = (heures) => Math.round((heures - Math.floor(heures)) * 60);

const formatHeuresAvecMinutes = (heures) => {
  if (heures === undefined || heures === null) return '0h 0min';
  const h = Math.floor(heures);
  const m = heuresEnMinutes(heures);
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}min`;
  return `${h}h ${m}min`;
};

const formatMinutes = (minutes) => {
  if (!minutes) return '0min';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount);

// --- MODALE DE BASE ---
const CustomModal = ({ show, onClose, title, children, footer }) => {
  if (!show) return null;
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0, color: '#1B019B' }}>{title}</h3>
          <button style={modalStyles.closeButton} onClick={onClose}><FaTimes /></button>
        </div>
        <div style={{ marginBottom: '20px' }}>{children}</div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// --- MODALE DE VALIDATION ---
const ValidationModal = ({ show, onClose, detection, formData, onFormChange, onSubmit, isBulk, dateDetection, detectionResults, config }) => {
  if (!show) return null;

  const totalHeures = isBulk
    ? detectionResults.reduce((acc, d) => acc + (config.arrondi_minutes === 0 ? d.heures_sup_exact : d.heures_sup), 0)
    : (config.arrondi_minutes === 0 ? detection?.heures_sup_exact : detection?.heures_sup);

  const totalMontant = isBulk
    ? detectionResults.reduce((acc, d) => acc + (config.arrondi_minutes === 0 ? d.montant_estime_exact : d.montant_estime), 0)
    : (config.arrondi_minutes === 0 ? detection?.montant_estime_exact : detection?.montant_estime);

  const sz = isBulk 
    ? { pad: '8px', radius: '8px', font: '13px', icon: '30px', iconFont: '14px', iconMr: '10px' }
    : { pad: '12px', radius: '10px', font: '14px', icon: '40px', iconFont: '18px', iconMr: '15px' };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{ ...modalStyles.content, maxWidth: isBulk ? '500px' : '650px' }} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0, color: '#1B019B' }}>
            {isBulk ? 'Nouvelle activité groupée' : 'Nouvelle activité'}
          </h3>
          <button style={modalStyles.closeButton} onClick={onClose}><FaTimes /></button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          {/* Champ Nom */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '13px' }}>
              Nom de l'activité <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              placeholder={isBulk ? `Ex: Heures sup du ${new Date(dateDetection).toLocaleDateString('fr-FR')}` : "Saisissez le nom de l'activité"}
              style={{ 
                width: '100%', 
                padding: sz.pad, 
                border: '2px solid #e0e0e0', 
                borderRadius: sz.radius, 
                fontSize: sz.font, 
                boxSizing: 'border-box' 
              }}
              value={formData.nom}
              onChange={(e) => onFormChange({ ...formData, nom: e.target.value })}
            />
          </div>

          {/* Champ Description */}
          <div style={{ marginBottom: isBulk ? '15px' : '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '13px' }}>
              Description <span style={{ color: '#999', fontWeight: 'normal' }}>(optionnel)</span>
            </label>
            <textarea
              placeholder="Description de l'activité"
              style={{ 
                width: '100%', 
                padding: sz.pad, 
                border: '2px solid #e0e0e0', 
                borderRadius: sz.radius, 
                fontSize: sz.font, 
                minHeight: isBulk ? '60px' : '100px', 
                fontFamily: 'inherit', 
                boxSizing: 'border-box', 
                resize: 'vertical' 
              }}
              value={formData.description}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
            />
          </div>

          {/* Récapitulatif */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', 
            padding: isBulk ? '15px' : '20px', 
            borderRadius: isBulk ? '12px' : '15px', 
            border: '2px solid #e0e0e0', 
            marginTop: isBulk ? '10px' : '15px' 
          }}>
            <p style={{ 
              margin: '0 0 10px 0', 
              color: '#1B019B', 
              fontWeight: '700', 
              fontSize: isBulk ? '14px' : '16px', 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px' 
            }}>
              {isBulk ? '📊 Récapitulatif groupé' : '📋 Récapitulatif'}
            </p>

            {!isBulk && detection && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '15px', 
                padding: '10px', 
                background: 'white', 
                borderRadius: '10px', 
                border: '1px solid #e0e0e0' 
              }}>
                <div style={{ 
                  width: sz.icon, 
                  height: sz.icon, 
                  background: '#1B019B', 
                  borderRadius: sz.radius, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginRight: sz.iconMr 
                }}>
                  <span style={{ color: 'white', fontSize: sz.iconFont }}>👤</span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block' }}>Employé</span>
                  <span style={{ fontWeight: '600', color: '#333', fontSize: '15px' }}>
                    {detection.employe_nom} {detection.employe_prenom}
                  </span>
                </div>
              </div>
            )}

            {isBulk && (
              <>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '10px', 
                  padding: '8px', 
                  background: 'white', 
                  borderRadius: '8px', 
                  border: '1px solid #e0e0e0' 
                }}>
                  <div style={{ width: '30px', height: '30px', background: '#1B019B', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <span style={{ color: 'white', fontSize: '14px' }}>📅</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>Date</span>
                    <span style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>
                      {new Date(dateDetection).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '10px', 
                  padding: '8px', 
                  background: 'white', 
                  borderRadius: '8px', 
                  border: '1px solid #e0e0e0' 
                }}>
                  <div style={{ width: '30px', height: '30px', background: '#17a2b8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <span style={{ color: 'white', fontSize: '14px' }}>👥</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>Employés</span>
                    <span style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>{detectionResults.length} employé(s)</span>
                  </div>
                </div>
              </>
            )}

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '15px', 
              padding: sz.pad, 
              background: 'white', 
              borderRadius: sz.radius, 
              border: '1px solid #e0e0e0' 
            }}>
              <div style={{ 
                width: sz.icon, 
                height: sz.icon, 
                background: '#28a745', 
                borderRadius: sz.radius, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginRight: sz.iconMr 
              }}>
                <span style={{ color: 'white', fontSize: sz.iconFont }}>⏱️</span>
              </div>
              <div>
                <span style={{ fontSize: isBulk ? '11px' : '12px', color: '#666', display: 'block' }}>Heures supplémentaires</span>
                <span style={{ fontWeight: '700', color: '#28a745', fontSize: isBulk ? '13px' : '16px' }}>
                  {formatHeuresAvecMinutes(totalHeures)}
                </span>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: sz.pad, 
              background: 'white', 
              borderRadius: sz.radius, 
              border: '1px solid #e0e0e0' 
            }}>
              <div style={{ 
                width: sz.icon, 
                height: sz.icon, 
                background: '#ffc107', 
                borderRadius: sz.radius, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginRight: sz.iconMr 
              }}>
                <span style={{ color: 'white', fontSize: sz.iconFont }}>💰</span>
              </div>
              <div>
                <span style={{ fontSize: isBulk ? '11px' : '12px', color: '#666', display: 'block' }}>Montant estimé</span>
                <span style={{ fontWeight: '700', color: '#333', fontSize: isBulk ? '13px' : '16px' }}>
                  {formatCurrency(totalMontant)}
                </span>
              </div>
            </div>
          </div>

          {isBulk && (
            <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '8px', borderLeft: '4px solid #1B019B' }}>
              <p style={{ margin: '0', color: '#1B019B', fontSize: '12px' }}>
                <span style={{ fontWeight: '600' }}>ℹ️ Info:</span> Une nouvelle activité sera créée pour tous les employés
              </p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
          <button 
            type="button"
            style={{ 
              backgroundColor: '#1B019B', 
              color: 'white', 
              border: 'none', 
              padding: isBulk ? '6px 12px' : '8px 15px', 
              borderRadius: '5px', 
              cursor: 'pointer', 
              fontSize: sz.font 
            }} 
            onClick={onSubmit}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const GestionHeuresSupPage = () => {
  const [activeTab, setActiveTab] = useState('detection');
  const [loading, setLoading] = useState(false);
  const [heuresSup, setHeuresSup] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();

  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const { user } = useAuth();

  const [dateDetection, setDateDetection] = useState(new Date().toISOString().split('T')[0]);
  const [detectionResults, setDetectionResults] = useState([]);
  const [detectionLoading, setDetectionLoading] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState('');
  const [showDetectionModal, setShowDetectionModal] = useState(false);

  const [config, setConfig] = useState({ arrondi_minutes: 0, heures_min_pour_declenchement: 0 });

  const [employes, setEmployes] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showBulkValidationModal, setShowBulkValidationModal] = useState(false);
  const [currentDetection, setCurrentDetection] = useState(null);
  const [validationForm, setValidationForm] = useState({ nom: '', description: '' });

  // Utilisation d'un debounce pour éviter trop d'appels API
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  // Styles réutilisables
  const inputStyle = { padding: '8px', borderRadius: '8px', border: '1px solid #ced4da' };
  const tableHeaderStyle = { padding: '12px' };

  useEffect(() => { fetchOrganisations(); }, []);
  useEffect(() => { if (selectedOrganisation) fetchEmployes(); }, [selectedOrganisation]);
  useEffect(() => { if (selectedOrganisation) fetchHeuresSup(1); }, [search, dateDebut, dateFin, pageSize, selectedOrganisation, activeTab]);
  
  // Détection automatique quand la date ou les seuils changent
  useEffect(() => {
    if (selectedOrganisation && activeTab === 'detection') {
      // Clear previous timeout
      if (debounceTimeout) clearTimeout(debounceTimeout);
      
      // Set new timeout to trigger detection after 500ms of inactivity
      const timeout = setTimeout(() => {
        detecterHeuresSup();
      }, 500);
      
      setDebounceTimeout(timeout);
      
      // Cleanup
      return () => clearTimeout(timeout);
    }
  }, [dateDetection, config.heures_min_pour_declenchement, config.arrondi_minutes, selectedOrganisation, activeTab]);

  const fetchOrganisations = async () => {
    try {
      const response = await axiosInstance.get('/organisations/');
      if (response.data.length > 0) {
        setSelectedOrganisation(response.data[0].id);
        setCurrentOrganisation(response.data[0]);
      }
    } catch (err) { console.error("Erreur organisations", err); }
  };

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
  };

  const fetchEmployes = async () => {
    try {
      const response = await axiosInstance.get('/employes/', {
        params: { organisation: selectedOrganisation, is_active: true, page_size: 100 }
      });
      setEmployes(response.data.results || []);
    } catch (err) { console.error("Erreur employés", err); }
  };

  const fetchHeuresSup = async (targetPage = 1) => {
    if (!selectedOrganisation) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage, search, date_debut: dateDebut, date_fin: dateFin,
        organisation: selectedOrganisation, page_size: pageSize === 'all' ? 1000 : pageSize
      });
      if (activeTab === 'validation') params.append('etat', 'en_attente');
      const response = await axiosInstance.get(`/pointage/heures-supplementaires/?${params}`);
      setHeuresSup(response.data.results);
      setTotalCount(response.data.count);
      setPagination({ totalPages: pageSize === 'all' ? 1 : Math.ceil(response.data.count / pageSize) });
      setPage(targetPage);
    } catch (err) { console.error("Erreur heures sup", err); }
    finally { setLoading(false); }
  };

  const detecterHeuresSup = async () => {
    if (!dateDetection || !selectedOrganisation) return;
    setDetectionLoading(true);
    setDetectionMessage('');
    setDetectionResults([]);
    try {
      const response = await axiosInstance.post('/pointage/heures-supplementaires/detecter/', {
        date: dateDetection, organisation: selectedOrganisation,
        seuil_min: config.heures_min_pour_declenchement, arrondi: config.arrondi_minutes
      });
      setDetectionResults(response.data.results || []);
      setDetectionMessage(response.data.message || 'Détection terminée');
      if (response.data.results?.length > 0) setShowDetectionModal(true);
    } catch (err) {
      console.error("Erreur détection", err);
      setDetectionMessage('Erreur lors de la détection des heures sup');
    } finally { setDetectionLoading(false); }
  };

  const validerDetection = (detection) => {
    setCurrentDetection(detection);
    setValidationForm({ nom: '', description: '' });
    setShowValidationModal(true);
  };

  const handleValidationSubmit = async () => {
    if (!validationForm.nom) {
      Swal.fire({ 
        icon: 'error', 
        title: 'Champ requis', 
        text: "Le nom de l'activité est requis" 
      });
      return;
    }
    
    try {
      const data = {
        employe_id: currentDetection.employe_id, 
        date: currentDetection.date,
        heures: config.arrondi_minutes === 0 ? currentDetection.heures_sup_exact : currentDetection.heures_sup,
        montant: config.arrondi_minutes === 0 ? currentDetection.montant_estime_exact : currentDetection.montant_estime,
        etat: 'approuve' // Ajout du statut approuvé
      };
      
      const activiteData = {
        nom: validationForm.nom, 
        date: currentDetection.date, 
        organisation: selectedOrganisation, 
        est_active: true,
        description: validationForm.description || `Heures supplémentaires du ${new Date(currentDetection.date).toLocaleDateString('fr-FR')}`,
        employes: [], 
        lieu: "", 
        heure_debut: null, 
        heure_fin: null
      };
      
      const activiteResponse = await axiosInstance.post('/pointage/activites/create/', activiteData);
      data.activite_id = activiteResponse.data.activite.id;
      
      await axiosInstance.post('/pointage/heures-supplementaires/creer-depuis-detection/', data);
      
      setDetectionResults(prev => prev.map(d => d.employe_id === currentDetection.employe_id ? { ...d, valide: true } : d));
      fetchHeuresSup();
      setShowValidationModal(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: "L'heure supplémentaire a été créée et approuvée avec succès.",
      });
    } catch (err) { 
      Swal.fire({ 
        icon: 'error', 
        title: 'Erreur', 
        text: err.response?.data?.error || err.message 
      });
    }
  };

  const validerToutesDetections = () => {
    setValidationForm({ nom: '', description: '' });
    setShowBulkValidationModal(true);
  };

  const handleBulkValidationSubmit = async () => {
    if (!validationForm.nom) {
      Swal.fire({ 
        icon: 'error', 
        title: 'Champ requis', 
        text: "Le nom de l'activité est requis" 
      });
      return;
    }
    
    try {
      const data = { 
        date: dateDetection, 
        organisation: selectedOrganisation, 
        arrondi: config.arrondi_minutes,
        etat: 'approuve' // Ajout du statut approuvé
      };
      
      const activiteData = {
        nom: validationForm.nom, 
        date: dateDetection, 
        organisation: selectedOrganisation, 
        est_active: true,
        description: validationForm.description || `Heures supplémentaires du ${new Date(dateDetection).toLocaleDateString('fr-FR')}`,
        employes: [], 
        lieu: "", 
        heure_debut: null, 
        heure_fin: null
      };
      
      const activiteResponse = await axiosInstance.post('/pointage/activites/create/', activiteData);
      data.activite_id = activiteResponse.data.activite.id;
      
      await axiosInstance.post('/pointage/heures-supplementaires/valider-toutes/', data);
      
      setShowBulkValidationModal(false);
      setShowDetectionModal(false);
      fetchHeuresSup();
      
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Les heures supplémentaires ont été créées et approuvées avec succès.',
      });
    } catch (err) { 
      Swal.fire({ 
        icon: 'error', 
        title: 'Erreur', 
        text: err.response?.data?.error || err.message 
      });
    }
  };

  const handleDeleteHeureSup = (id) => {
    Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: 'Cette action est irréversible !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer !',
      cancelButtonText: 'Annuler'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/pointage/heures-supplementaires/${id}/`);
          fetchHeuresSup();
          Swal.fire({
            icon: 'success',
            title: 'Supprimé !',
            text: "L'heure supplémentaire a été supprimée.",
          });
        } catch (err) {
          Swal.fire({ 
            icon: 'error', 
            title: 'Erreur', 
            text: "Impossible de supprimer l'heure supplémentaire." 
          });
        }
      }
    });
  };

  const handleApproveHeureSup = async (id) => {
    try {
      await axiosInstance.patch(`/pointage/heures-supplementaires/${id}/`, { etat: 'approuve' });
      
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'La demande a été approuvée avec succès.',
      });
      
      fetchHeuresSup();
    } catch (err) { 
      Swal.fire({ 
        icon: 'error', 
        title: 'Erreur', 
        text: "Impossible d'approuver la demande." 
      });
    }
  };

  const handleRejectHeureSup = async (id) => {
    try {
      await axiosInstance.patch(`/pointage/heures-supplementaires/${id}/`, { etat: 'rejete' });
      
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'La demande a été rejetée avec succès.',
      });
      
      fetchHeuresSup();
    } catch (err) { 
      Swal.fire({ 
        icon: 'error', 
        title: 'Erreur', 
        text: 'Impossible de rejeter la demande.' 
      });
    }
  };

  const getEtatBadge = (etat) => {
    const map = {
      'en_attente': { bg: '#ffc107', color: '#000', label: 'En attente' },
      'approuve': { bg: '#28a745', color: '#fff', label: 'Approuvé' },
      'rejete': { bg: '#dc3545', color: '#fff', label: 'Rejeté' }
    };
    const s = map[etat] || map['en_attente'];
    return <span style={{ backgroundColor: s.bg, color: s.color, padding: '3px 8px', borderRadius: '12px', fontSize: '12px' }}>{s.label}</span>;
  };

  // Composant Filtres réutilisable
  const FilterRow = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FaSearch style={{ marginRight: '8px', color: '#1B019B' }} />
        <input 
          type="text" 
          placeholder="Rechercher employé..." 
          style={{ ...inputStyle, width: '250px' }} 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FaCalendarAlt style={{ marginRight: '8px', color: '#1B019B' }} />
        <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} style={inputStyle} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px', color: '#1B019B' }}>à</span>
        <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} style={inputStyle} />
      </div>
    </div>
  );

  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>
      <div className="text-center mb-3">
        <h3 style={styles.header}>Gestion des Heures Supplémentaires</h3>
        <hr style={styles.hr} />
      </div>

      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange} 
          selectedOrganisation={selectedOrganisation} 
        />
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={{ color: "#1B019B", fontSize: "28px", fontWeight: "bold", marginBottom: "20px" }}>
            ⏱️ GESTION HEURES SUP - {currentOrganisation.nom}
          </h1>

          {/* Tabs */}
          <div style={tabStyles.container}>
            <button 
              style={{...tabStyles.tab, ...(activeTab === 'detection' ? tabStyles.activeTab : {})}}
              onClick={() => setActiveTab('detection')}
            >
              <FaCalculator style={{ marginRight: '8px' }} />Détection automatique et Validation
            </button>
            {/* <button 
              style={{...tabStyles.tab, ...(activeTab === 'validation' ? tabStyles.activeTab : {})}}
              onClick={() => setActiveTab('validation')}
            >
              <FaCheckCircle style={{ marginRight: '8px' }} />Validation
            </button> */}
            <button 
              style={{...tabStyles.tab, ...(activeTab === 'historique' ? tabStyles.activeTab : {})}}
              onClick={() => setActiveTab('historique')}
            >
              <FaClock style={{ marginRight: '8px' }} />Historique
            </button>
          </div>

          {/* Tab Détection */}
          {activeTab === 'detection' && (
            <div className="card" style={{ borderRadius: '15px', border: '1px solid #e0e0e0', padding: '20px', marginBottom: '20px' }}>
              <h5 style={{ color: '#1B019B', marginBottom: '15px' }}>
                <FaClock style={{ marginRight: '8px' }} />Détecter les heures supplémentaires
              </h5>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date à analyser</label>
                  <input 
                    type="date" 
                    style={{ width: '100%', ...inputStyle }} 
                    value={dateDetection}
                    onChange={(e) => setDateDetection(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Seuil minimum <span style={{ color: '#666', fontSize: '0.9em' }}>(en heures décimales)</span>
                  </label>
                  <input 
                    type="number" 
                    step="0.1" 
                    min="0" 
                    style={{ width: '100%', ...inputStyle }} 
                    value={config.heures_min_pour_declenchement} 
                    onChange={(e) => setConfig({ ...config, heures_min_pour_declenchement: parseFloat(e.target.value) || 0 })} 
                  />
                  <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                    {config.heures_min_pour_declenchement > 0 
                      ? `Soit ${formatHeuresAvecMinutes(config.heures_min_pour_declenchement)}` 
                      : 'Ne détecter que les dépassements supérieurs à ce seuil'}
                  </small>
                </div>
              </div>
              
              {/* Indicateur de chargement automatique */}
              {detectionLoading && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ color: '#1B019B' }}>🔍 Analyse en cours...</span>
                </div>
              )}
              
              {/* Affichage des résultats sans bouton */}
              {detectionMessage && !detectionLoading && (
                <div style={{ marginTop: '15px' }}>
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: detectionMessage.includes('succès') || detectionResults.length > 0 ? '#d4edda' : '#d1ecf1', 
                    color: detectionMessage.includes('succès') || detectionResults.length > 0 ? '#155724' : '#0c5460', 
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    {detectionMessage}
                  </div>
                  
                  {/* Affichage direct des résultats s'il y en a */}
                  {detectionResults.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h6 style={{ margin: 0, color: '#1B019B' }}>📋 Résultats de la détection</h6>
                        <button 
                          style={{ backgroundColor: '#1B019B', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer' }} 
                          onClick={validerToutesDetections}
                        >
                          Valider toutes les détections
                        </button>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Employé</th>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Heures travail</th>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Heures sup</th>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Montant</th>
                              <th style={{ padding: '10px', textAlign: 'left' }}>Action</th>
                             </tr>
                          </thead>
                          <tbody>
                            {detectionResults.map(d => (
                              <tr key={d.employe_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                <td style={{ padding: '10px' }}>{d.employe_nom} {d.employe_prenom}</td>
                                <td style={{ padding: '10px' }}><strong>{formatHeuresAvecMinutes(d.heures_travaillees)}</strong></td>
                                <td style={{ padding: '10px' }}>
                                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                                    {formatHeuresAvecMinutes(config.arrondi_minutes === 0 ? d.heures_sup_exact : d.heures_sup)}
                                  </span>
                                </td>
                                <td style={{ padding: '10px' }}>
                                  {formatCurrency(config.arrondi_minutes === 0 ? d.montant_estime_exact : d.montant_estime)}
                                </td>
                                <td style={{ padding: '10px' }}>
                                  {d.valide ? (
                                    <span style={{ color: '#28a745' }}><FaCheck style={{ marginRight: '5px' }} />Validé</span>
                                  ) : (
                                    <button 
                                      style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }} 
                                      onClick={() => validerDetection(d)}
                                    >
                                      Valider
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab Validation */}
          {activeTab === 'validation' && (
            <div className="card" style={{ borderRadius: '15px', border: '1px solid #e0e0e0', padding: '20px' }}>
              <h5 style={{ color: '#1B019B', marginBottom: '15px' }}>
                <FaHourglassHalf style={{ marginRight: '8px' }} />Demandes en attente de validation
              </h5>
              <FilterRow />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1B019B', color: 'white' }}>
                      <th style={tableHeaderStyle}>Employé</th>
                      <th style={tableHeaderStyle}>Date</th>
                      <th style={tableHeaderStyle}>Heures</th>
                      <th style={tableHeaderStyle}>Montant</th>
                      <th style={tableHeaderStyle}>Activité</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heuresSup.map(h => (
                      <tr key={h.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={tableHeaderStyle}>{h.employe_nom} {h.employe_prenom}</td>
                        <td style={tableHeaderStyle}>{new Date(h.date).toLocaleDateString('fr-FR')}</td>
                        <td style={{ ...tableHeaderStyle, fontWeight: 'bold' }}>{formatHeuresAvecMinutes(h.heures)}</td>
                        <td style={tableHeaderStyle}>{formatCurrency(h.montant)}</td>
                        <td style={tableHeaderStyle}>{h.activite_nom || '-'}</td>
                        <td style={tableHeaderStyle}>
                          <button 
                            style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', marginRight: '5px', cursor: 'pointer' }} 
                            onClick={() => handleApproveHeureSup(h.id)} 
                            title="Approuver"
                          >
                            <FaCheck />
                          </button>
                          <button 
                            style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }} 
                            onClick={() => handleRejectHeureSup(h.id)} 
                            title="Rejeter"
                          >
                            <FaTimes />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {heuresSup.length === 0 && (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>Aucune demande en attente</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab Historique */}
          {activeTab === 'historique' && (
            <div className="card" style={{ borderRadius: '15px', border: '1px solid #e0e0e0', padding: '20px' }}>
              <h5 style={{ color: '#1B019B', marginBottom: '15px' }}>
                <FaClock style={{ marginRight: '8px' }} />Historique des heures supplémentaires
              </h5>
              <FilterRow />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1B019B', color: 'white' }}>
                      <th style={tableHeaderStyle}>Employé</th>
                      <th style={tableHeaderStyle}>Date</th>
                      <th style={tableHeaderStyle}>Heures</th>
                      <th style={tableHeaderStyle}>Montant</th>
                      <th style={tableHeaderStyle}>Activité</th>
                      <th style={tableHeaderStyle}>Statut</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heuresSup.map(h => (
                      <tr key={h.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={tableHeaderStyle}>{h.employe_nom} {h.employe_prenom}</td>
                        <td style={tableHeaderStyle}>{new Date(h.date).toLocaleDateString('fr-FR')}</td>
                        <td style={{ ...tableHeaderStyle, fontWeight: 'bold' }}>{formatHeuresAvecMinutes(h.heures)}</td>
                        <td style={tableHeaderStyle}>{formatCurrency(h.montant)}</td>
                        <td style={tableHeaderStyle}>{h.activite_nom || '-'}</td>
                        <td style={tableHeaderStyle}>{getEtatBadge(h.etat)}</td>
                        <td style={tableHeaderStyle}>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            {h.etat !== 'approuve' && (
                              <button 
                                style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }} 
                                onClick={() => handleApproveHeureSup(h.id)} 
                                title="Approuver"
                              >
                                <FaCheck />
                              </button>
                            )}
                            {h.etat !== 'rejete' && (
                              <button 
                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }} 
                                onClick={() => handleRejectHeureSup(h.id)} 
                                title="Rejeter"
                              >
                                <FaTimes />
                              </button>
                            )}
                            <button 
                              style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }} 
                              onClick={() => handleDeleteHeureSup(h.id)} 
                              title="Supprimer"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {heuresSup.length === 0 && (
                      <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>Aucune heure supplémentaire enregistrée</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MODALES */}
          
          {/* Modal de validation individuelle */}
          <ValidationModal
            show={showValidationModal}
            onClose={() => setShowValidationModal(false)}
            detection={currentDetection}
            formData={validationForm}
            onFormChange={setValidationForm}
            onSubmit={handleValidationSubmit}
            isBulk={false}
            dateDetection={dateDetection}
            detectionResults={detectionResults}
            config={config}
          />

          {/* Modal de validation groupée */}
          <ValidationModal
            show={showBulkValidationModal}
            onClose={() => setShowBulkValidationModal(false)}
            detection={null}
            formData={validationForm}
            onFormChange={setValidationForm}
            onSubmit={handleBulkValidationSubmit}
            isBulk={true}
            dateDetection={dateDetection}
            detectionResults={detectionResults}
            config={config}
          />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#6c757d' }}>Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default GestionHeuresSupPage;